import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { apiService } from '../services/api';
import { resolveApiError } from '../utils/i18n';

interface Props {
  open: boolean;
  currentEmail?: string;
  onVerified: () => void;
  onClose: () => void;
}

export const EmailVerificationModal: React.FC<Props> = ({ open, currentEmail, onVerified, onClose }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(currentEmail ?? '');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    setError('');
    setLoading(true);
    try {
      await apiService.users.requestEmailVerification(email || undefined);
      setSent(true);
    } catch (err: any) {
      setError(resolveApiError(err?.response?.data?.errorCode, err?.response?.data?.message, t));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmed = () => {
    onVerified();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            {t('emailVerification.title', '電郵驗證')}
          </DialogTitle>
          <DialogDescription>
            {t('emailVerification.desc', '付款前需完成電郵驗證，以便接收收據及帳單通知。')}
          </DialogDescription>
        </DialogHeader>

        {!sent ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('emailVerification.emailLabel', '電郵地址')}</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={onClose} disabled={loading}>
                {t('common.cancel', '取消')}
              </Button>
              <Button onClick={handleSend} disabled={loading || !email.trim()}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t('emailVerification.sendBtn', '發送驗證連結')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3 py-4 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
              <p className="text-sm text-muted-foreground">
                {t('emailVerification.sentDesc', '驗證連結已發送至 ')}
                <span className="font-medium text-foreground">{email}</span>
                {t('emailVerification.sentDesc2', '，請點擊連結完成驗證後，回到此頁點擊「已驗證」繼續。')}
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={handleSend} disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t('emailVerification.resend', '重新發送')}
              </Button>
              <Button onClick={handleConfirmed}>
                {t('emailVerification.confirmed', '已驗證，繼續付款')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
